import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { StockingServiceService } from '../../stocking-service.service';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { DeliveryEmailService } from '../delivery-email.service';

// Approve or reject a møtemat order a customer placed in the portal.
//
// A portal order sits at 'Pending approval' and has sent NOTHING to logistics or
// IL. Approving here is what fires those mails — which is why this dialog shows
// the order against the CURRENT menu first: the customer chose from a week that
// may have been redrawn since, and an operator must see that before confirming.
@Component({
  selector: 'app-approve-delivery-dialog',
  templateUrl: './approve-delivery-dialog.component.html',
  styleUrl: './approve-delivery-dialog.component.css'
})
export class ApproveDeliveryDialogComponent {

  order: any;
  loading = true;
  working = false;

  settings: any = null;
  menuMissing = false;
  // Lines whose product is no longer the one the menu carries for that product
  // type — the "the menu moved under them" case.
  changed: any[] = [];

  rejecting = false;
  rejectReason = '';

  approverName = '';
  approverMail = '';

  constructor(
    public dialogRef: MatDialogRef<ApproveDeliveryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private stockingService: StockingServiceService,
    private deliveryEmails: DeliveryEmailService,
    private notificationService: NotificationService,
    private auth: AuthService
  ) {
    // The table hands over its DISPLAY row: `date` has been reformatted to
    // DD.MM.YYYY and two derived columns have been bolted on. Both would be
    // written straight back to Firestore by the approve/reject merge, and a
    // date in that shape re-parses as garbage — so rebuild a clean order first.
    const row = data.delivery;
    this.order = {
      ...row,
      date: row.dateUnix
        ? moment.unix(row.dateUnix).format()
        : moment(row.date, 'DD.MM.YYYY').format()
    };
    delete this.order.productsString;
    delete this.order.sourceLabel;

    const authSub = this.auth.user$.subscribe((user: any) => {
      if (!user) return;
      this.approverMail = user.email;
      this.approverName = user.Name;
      authSub.unsubscribe();
    });

    this.stockingService.getStockingSettings$().subscribe((snapshot: any) => {
      this.settings = snapshot.data();
      this.checkAgainstMenu();
    });
  }

  // Compare each ordered line with what the menu now says for that product type.
  private checkAgainstMenu() {
    const date = moment(this.order.date);
    const menuSub = this.stockingService.getMenu$(date.week(), date.year()).subscribe((menu: any) => {
      menuSub.unsubscribe();

      if (!menu || !menu.data) {
        this.menuMissing = true;
        this.loading = false;
        return;
      }

      this.changed = (this.order.products || []).reduce((acc, line) => {
        const row = menu.data[line.productType];
        const current = row && row.product ? row.product : null;
        if (current && current.id !== line.productId) {
          acc.push({ ordered: line.product, now: current.name, productType: line.productType });
        }
        return acc;
      }, []);

      this.loading = false;
    });
  }

  productLines() {
    return this.order.products || [];
  }

  deliveryDate() {
    return moment(this.order.date).format('dddd, DD.MM.YYYY');
  }

  kr(v: number): string {
    return (Number(v) || 0).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  async approve() {
    if (this.working) return;
    this.working = true;

    // The order the mails describe is the approved one, so status and approval
    // are stamped before anything is sent.
    const approved = {
      ...this.order,
      status: 'Sheduled',
      approval: {
        by: this.approverMail,
        at: moment().unix(),
        reason: ''
      }
    };

    try {
      // Same mails an operator-created delivery produces — see
      // DeliveryEmailService. Logistics is scheduled for 08:00 the day before;
      // IL only hears about food that is made fresh.
      const logsticsEmailRef = await this.deliveryEmails.sendLogisticsEmail(approved, this.settings, false);
      if (logsticsEmailRef) approved.logsticsEmailRef = logsticsEmailRef;

      const ilEmailRef = await this.deliveryEmails.sendIlEmail(approved, this.settings, false);
      if (ilEmailRef) approved.ilEmailRef = ilEmailRef;

      await this.deliveryEmails.approvedEmail(approved);

      await this.stockingService.updateExtraDelivery(approved, this.order.id);

      this.notificationService.showNotification('success', `Order #${this.order.deliveryNumber} approved`);
      this.dialogRef.close('approved');
    } catch (error) {
      console.error('Error approving delivery: ', error);
      this.notificationService.showNotification('error', 'Could not approve the order');
      this.working = false;
    }
  }

  async reject() {
    if (this.working) return;
    if (!this.rejectReason.trim()) {
      this.notificationService.showNotification('error', 'Give a reason — the customer is told what it says');
      return;
    }
    this.working = true;

    const rejected = {
      ...this.order,
      status: 'Rejected',
      approval: {
        by: this.approverMail,
        at: moment().unix(),
        reason: this.rejectReason.trim()
      }
    };

    try {
      await this.deliveryEmails.rejectedEmail(rejected, rejected.approval.reason);
      await this.stockingService.updateExtraDelivery(rejected, this.order.id);

      this.notificationService.showNotification('success', `Order #${this.order.deliveryNumber} rejected`);
      this.dialogRef.close('rejected');
    } catch (error) {
      console.error('Error rejecting delivery: ', error);
      this.notificationService.showNotification('error', 'Could not reject the order');
      this.working = false;
    }
  }

  close() {
    this.dialogRef.close();
  }
}
